import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, useParams } from 'react-router-dom';
import { recruiterAPI, jobsAPI } from '../services/api';
import RecruiterSidebar from '../components/RecruiterSidebar';
import '../styles/theme.css';
import './PostJob.css'; // reuse same styles

function EditJob() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { jobId } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [benefitInput, setBenefitInput] = useState('');
  const [questionInput, setQuestionInput] = useState('');

  const [formData, setFormData] = useState({
    title: '', company: '', department: '', employment_type: 'Full-time',
    city: '', country: 'South Africa', work_mode: 'On-site',
    description: '', responsibilities: '', requirements: '', preferred_skills: '',
    salary_min: '', salary_max: '', salary_currency: 'ZAR', salary_negotiable: false,
    benefits: [], experience_level: '', education_requirement: '',
    application_deadline: '', required_documents: [], contact_email: '',
    screening_questions: []
  });

  useEffect(() => {
    loadJob();
  }, [jobId]);

  const loadJob = async () => {
    try {
      const res = await jobsAPI.getJob(jobId);
      const j = res.data;
      // Format deadline for date input (YYYY-MM-DD)
      let deadline = '';
      if (j.application_deadline) {
        deadline = new Date(j.application_deadline).toISOString().split('T')[0];
      }
      setFormData({
        title: j.title || '',
        company: j.company || '',
        department: j.department || '',
        employment_type: j.employment_type || 'Full-time',
        city: j.city || '',
        country: j.country || 'South Africa',
        work_mode: j.work_mode || 'On-site',
        description: j.description || '',
        responsibilities: j.responsibilities || '',
        requirements: j.requirements || '',
        preferred_skills: j.preferred_skills || '',
        salary_min: j.salary_min || '',
        salary_max: j.salary_max || '',
        salary_currency: j.salary_currency || 'ZAR',
        salary_negotiable: j.salary_negotiable || false,
        benefits: Array.isArray(j.benefits) ? j.benefits : [],
        experience_level: j.experience_level || '',
        education_requirement: j.education_requirement || '',
        application_deadline: deadline,
        required_documents: Array.isArray(j.required_documents) ? j.required_documents : [],
        contact_email: j.contact_email || '',
        screening_questions: Array.isArray(j.screening_questions) ? j.screening_questions : [],
      });
    } catch (err) {
      if (err.response?.status === 401) navigate('/recruiter/login');
      else if (err.response?.status === 403) navigate('/recruiter/dashboard');
      else setError('Failed to load job details.');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDocumentToggle = (doc) => {
    setFormData(prev => ({
      ...prev,
      required_documents: prev.required_documents.includes(doc)
        ? prev.required_documents.filter(d => d !== doc)
        : [...prev.required_documents, doc]
    }));
  };

  const addBenefit = () => {
    if (benefitInput.trim()) {
      setFormData(prev => ({ ...prev, benefits: [...prev.benefits, benefitInput.trim()] }));
      setBenefitInput('');
    }
  };

  const removeBenefit = (i) => setFormData(prev => ({ ...prev, benefits: prev.benefits.filter((_, idx) => idx !== i) }));

  const addQuestion = () => {
    if (questionInput.trim()) {
      setFormData(prev => ({ ...prev, screening_questions: [...prev.screening_questions, questionInput.trim()] }));
      setQuestionInput('');
    }
  };

  const removeQuestion = (i) => setFormData(prev => ({ ...prev, screening_questions: prev.screening_questions.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let formattedDeadline = null;
      if (formData.application_deadline) {
        const [year, month, day] = formData.application_deadline.split('-').map(Number);
        // Set to end of day (23:59:59) in local time to avoid UTC timezone off-by-one
        const date = new Date(year, month - 1, day, 23, 59, 59);
        formattedDeadline = date.toISOString();
      }
      await recruiterAPI.updateJob(jobId, {
        ...formData,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        application_deadline: formattedDeadline,
      });
      setSuccess(true);
      setTimeout(() => navigate('/recruiter/dashboard'), 1200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) setError(detail.map(e => `${e.loc.join(' → ')}: ${e.msg}`).join('\n'));
      else setError(detail || 'Failed to update job.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-content">
          <div className="empty-state"><i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i><p>Loading job...</p></div>
        </div>
      </main>
    </div>
  );

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <button className="topbar-btn" onClick={() => navigate('/recruiter/dashboard')}><i className="fas fa-arrow-left"></i></button>
          <span className="topbar-title">Edit Job</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme"><i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i></button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/recruiter/dashboard')}>Cancel</button>
          </div>
        </div>

        <div className="portal-content">
          {success && (
            <div className="alert alert-success" style={{ marginBottom: '1.5rem' }}>
              <i className="fas fa-check-circle"></i> Job updated successfully. Redirecting...
            </div>
          )}

          <form onSubmit={handleSubmit} className="post-job-form-inner">

            {/* Basic Info */}
            <section className="form-section">
              <h2>Basic Job Information</h2>
              <div className="form-row">
                <div className="input-group">
                  <label>Job Title *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleChange} required placeholder="e.g., Data Analyst" />
                </div>
                <div className="input-group">
                  <label>Company Name</label>
                  <input type="text" name="company" value={formData.company} readOnly style={{ background: 'var(--surface-2)', cursor: 'not-allowed', opacity: 0.8 }} />
                  <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Locked to your registered company</small>
                </div>
              </div>
              <div className="form-row">
                <div className="input-group">
                  <label>Department</label>
                  <input type="text" name="department" value={formData.department} onChange={handleChange} placeholder="IT, Marketing, HR..." />
                </div>
                <div className="input-group">
                  <label>Employment Type *</label>
                  <select name="employment_type" value={formData.employment_type} onChange={handleChange} required>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                    <option value="Freelance">Freelance</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Location */}
            <section className="form-section">
              <h2>Location</h2>
              <div className="form-row">
                <div className="input-group">
                  <label>City</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="Johannesburg" />
                </div>
                <div className="input-group">
                  <label>Country</label>
                  <input type="text" name="country" value={formData.country} onChange={handleChange} />
                </div>
                <div className="input-group">
                  <label>Work Mode *</label>
                  <select name="work_mode" value={formData.work_mode} onChange={handleChange} required>
                    <option value="On-site">On-site</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Description */}
            <section className="form-section">
              <h2>Job Description</h2>
              <div className="input-group">
                <label>Overview *</label>
                <textarea name="description" value={formData.description} onChange={handleChange} required rows="4" placeholder="Overview of the role..." />
              </div>
              <div className="input-group">
                <label>Responsibilities</label>
                <textarea name="responsibilities" value={formData.responsibilities} onChange={handleChange} rows="6" placeholder="List main tasks (one per line)" />
              </div>
            </section>

            {/* Skills */}
            <section className="form-section">
              <h2>Required Skills & Qualifications</h2>
              <div className="input-group">
                <label>Required Skills *</label>
                <textarea name="requirements" value={formData.requirements} onChange={handleChange} required rows="5" placeholder="Mandatory requirements (one per line)" />
              </div>
              <div className="input-group">
                <label>Preferred Skills</label>
                <textarea name="preferred_skills" value={formData.preferred_skills} onChange={handleChange} rows="4" placeholder="Nice-to-have skills" />
              </div>
            </section>

            {/* Salary */}
            <section className="form-section">
              <h2>Salary Information</h2>
              <div className="form-row">
                <div className="input-group">
                  <label>Minimum Salary</label>
                  <input type="number" name="salary_min" value={formData.salary_min} onChange={handleChange} placeholder="25000" />
                </div>
                <div className="input-group">
                  <label>Maximum Salary</label>
                  <input type="number" name="salary_max" value={formData.salary_max} onChange={handleChange} placeholder="40000" />
                </div>
                <div className="input-group">
                  <label>Currency</label>
                  <select name="salary_currency" value={formData.salary_currency} onChange={handleChange}>
                    <option value="ZAR">ZAR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>
              <div className="checkbox-group">
                <label><input type="checkbox" name="salary_negotiable" checked={formData.salary_negotiable} onChange={handleChange} /> Salary is negotiable</label>
              </div>
              <div className="input-group">
                <label>Benefits</label>
                <div className="add-item-group">
                  <input type="text" value={benefitInput} onChange={e => setBenefitInput(e.target.value)} placeholder="e.g., Medical aid" onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addBenefit())} />
                  <button type="button" onClick={addBenefit} className="btn-add">Add</button>
                </div>
                <div className="items-list">
                  {formData.benefits.map((b, i) => (
                    <div key={i} className="item-tag">{b}<button type="button" onClick={() => removeBenefit(i)}>×</button></div>
                  ))}
                </div>
              </div>
            </section>

            {/* Experience & Education */}
            <section className="form-section">
              <h2>Experience & Education</h2>
              <div className="form-row">
                <div className="input-group">
                  <label>Experience Level</label>
                  <select name="experience_level" value={formData.experience_level} onChange={handleChange}>
                    <option value="">Select level</option>
                    <option value="Entry Level">Entry Level</option>
                    <option value="Junior">Junior</option>
                    <option value="Mid-Level">Mid-Level</option>
                    <option value="Senior">Senior</option>
                    <option value="Executive">Executive</option>
                  </select>
                </div>
                <div className="input-group">
                  <label>Education Requirement</label>
                  <select name="education_requirement" value={formData.education_requirement} onChange={handleChange}>
                    <option value="">Select requirement</option>
                    <option value="Not required">Not required</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Bachelor's degree">Bachelor's degree</option>
                    <option value="Master's degree">Master's degree</option>
                    <option value="PhD">PhD</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Application Details */}
            <section className="form-section">
              <h2>Application Details</h2>
              <div className="form-row">
                <div className="input-group">
                  <label>Application Deadline</label>
                  <input type="date" name="application_deadline" value={formData.application_deadline} onChange={handleChange} />
                </div>
                <div className="input-group">
                  <label>Contact Email</label>
                  <input type="email" name="contact_email" value={formData.contact_email} onChange={handleChange} placeholder="hr@company.com" />
                </div>
              </div>
              <div className="input-group">
                <label>Required Documents</label>
                <div className="checkbox-group">
                  {[
                    'CV',
                    'Academic Transcript',
                    'Matric Certificate',
                    'Degree Certificate',
                    'Diploma Certificate',
                    'Cover Letter',
                    'Reference Letter',
                  ].map(doc => (
                    <label key={doc}><input type="checkbox" checked={formData.required_documents.includes(doc)} onChange={() => handleDocumentToggle(doc)} /> {doc}</label>
                  ))}
                </div>
              </div>
            </section>

            {/* Screening Questions */}
            <section className="form-section">
              <h2>Screening Questions</h2>
              <div className="input-group">
                <label>Screening questions</label>
                <div className="add-item-group">
                  <input type="text" value={questionInput} onChange={e => setQuestionInput(e.target.value)} placeholder="e.g., Do you have 2+ years Python experience?" onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addQuestion())} />
                  <button type="button" onClick={addQuestion} className="btn-add">Add</button>
                </div>
                <div className="items-list">
                  {formData.screening_questions.map((q, i) => (
                    <div key={i} className="item-tag">{q}<button type="button" onClick={() => removeQuestion(i)}>×</button></div>
                  ))}
                </div>
              </div>
            </section>

            {error && <div className="alert alert-error" style={{ whiteSpace: 'pre-wrap' }}>{error}</div>}

            <div className="form-actions">
              <button type="button" onClick={() => navigate('/recruiter/dashboard')} className="btn btn-secondary">Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : <><i className="fas fa-save"></i> Save Changes</>}
              </button>
            </div>

          </form>
        </div>
      </main>
    </div>
  );
}

export default EditJob;
